/**
 * Layout toggles — show and hide the workbench regions from one place.
 *
 * The app bar's `Layout` button opens a popover with one tile per region
 * the active layout has: the primary sidebar, the bottom panel, the
 * secondary sidebar. A tile runs the substrate's own toggle command for
 * its region (`shell.togglePrimarySidebar`, `shell.togglePanel`,
 * `shell.toggleSecondarySidebar` — the `shell.toggleSlot` capability with a
 * fixed slot, already bound to Cmd/Ctrl+B, +J and +L by the default keymap),
 * so the popover is a picture of commands that exist rather than a second
 * set. Building it found the capability closing a dock's *tab* instead of
 * hiding the dock; the substrate collapses the pane now (1.33.0).
 *
 * The controller holds no visibility of its own: every tile reads the live
 * state on open and after each toggle — the primary sidebar from the dock
 * controller, a dock from its split pane's collapse — and the `shell.view.*`
 * events refresh an open popover when a region changes under it (closing a
 * panel's last tab collapses the panel without any tile being pressed).
 */

import { COMMAND_IDS } from '@punica/editor';

import { sidebarDock } from '../sidebarDock/index.js';
import { createLayoutPopover, type LayoutPopoverHandle, type LayoutTile } from './popover.js';

import layoutTogglesCss from './layoutToggles.css';

const STYLE_ID = 'shell-classic-layout-toggles-style';
/** The app bar item the profile declares for this; the popover anchors to it. */
export const APP_BAR_ITEM_ID = 'appbar-layout';

export const OPEN_MENU_COMMAND = 'shell.layout.openMenu';

type RegionId = 'primarySidebar' | 'panel' | 'secondarySidebar';

interface RegionSpec {
  id: RegionId;
  label: string;
  icon: string;
  /** The substrate command that toggles the region. */
  command: string;
}

const REGIONS: readonly RegionSpec[] = [
  {
    id: 'primarySidebar',
    label: 'Sidebar',
    icon: 'fa-solid fa-sidebar',
    command: COMMAND_IDS.SHELL.TOGGLE_PRIMARY_SIDEBAR
  },
  {
    id: 'panel',
    label: 'Panel',
    icon: 'fa-regular fa-window-maximize fa-rotate-180',
    command: COMMAND_IDS.SHELL.TOGGLE_PANEL
  },
  {
    id: 'secondarySidebar',
    label: 'Right sidebar',
    icon: 'fa-solid fa-sidebar-flip',
    command: COMMAND_IDS.SHELL.TOGGLE_SECONDARY_SIDEBAR
  }
];

function layout(): any {
  return (globalThis as any).punica?.shell?.Layout?.manager ?? null;
}
function commands(): any {
  return (globalThis as any).punica?.kernel?.Commands?.manager ?? null;
}
function events(): any {
  return (globalThis as any).punica?.kernel?.Events?.manager ?? null;
}

export class LayoutTogglesController {
  #popover: LayoutPopoverHandle | null = null;
  #unsubscribers: Array<() => void> = [];
  #started = false;

  /**
   * Register the menu command and follow the view events. Started by the app
   * bar module, so a profile without an app bar (the agent window, which has
   * its own layout button) gets neither.
   */
  start(): void {
    if (this.#started) return;
    const cmds = commands();
    if (!cmds?.registerCommand) return;
    this.#started = true;
    this.#injectStyle();

    cmds.registerCommand(OPEN_MENU_COMMAND, {
      title: 'Layout',
      execute: (anchor?: unknown) => this.openMenu(anchor as HTMLElement | undefined)
    });

    const bus = events();
    if (bus?.subscribe) {
      for (const name of ['shell.view.revealed', 'shell.view.hidden', 'shell.view.moved']) {
        const unsub = bus.subscribe(name, () => this.#popover?.refresh());
        if (typeof unsub === 'function') this.#unsubscribers.push(unsub);
      }
    }
  }

  stop(): void {
    for (const unsub of this.#unsubscribers) unsub();
    this.#unsubscribers = [];
    commands()?.disposeCommand?.(OPEN_MENU_COMMAND);
    this.#popover?.dispose();
    this.#popover = null;
    this.#started = false;
  }

  /** The regions present in the active layout, in tile order. */
  regions(): RegionSpec[] {
    return REGIONS.filter((region) => this.#hasSlot(region.id));
  }

  #hasSlot(region: RegionId): boolean {
    try {
      return !!layout()?.getSlot?.(region);
    } catch {
      return false;
    }
  }

  isVisible(region: RegionId): boolean {
    if (region === 'primarySidebar') {
      try {
        return sidebarDock.isVisible();
      } catch {
        return false;
      }
    }
    try {
      return !layout()?.isSlotCollapsed?.(region);
    } catch {
      return true;
    }
  }

  /** Run the region's toggle command; the popover re-reads when it settles. */
  toggle(region: RegionId): Promise<void> {
    const spec = REGIONS.find((r) => r.id === region);
    if (!spec || !this.#hasSlot(region)) return Promise.resolve();
    let result: unknown;
    try {
      result = commands()?.executeCommand?.(spec.command);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[LayoutToggles] toggle failed', region, err);
    }
    return Promise.resolve(result)
      .catch(() => undefined)
      .then(() => this.#popover?.refresh());
  }

  /** Open (or close) the popover under the app bar's Layout button. */
  openMenu(anchor?: HTMLElement): void {
    const button =
      anchor instanceof HTMLElement
        ? anchor
        : (document.querySelector(
            `#punica-appbar [data-appbar-item='${APP_BAR_ITEM_ID}'] button`
          ) as HTMLElement | null);
    if (!button) return;
    if (!this.#popover) {
      this.#popover = createLayoutPopover({
        title: 'Layout',
        tiles: () => this.#tiles()
      });
    }
    this.#popover.toggleOpen(button);
  }

  #tiles(): LayoutTile[] {
    return this.regions().map((region) => ({
      id: region.id,
      label: region.label,
      icon: region.icon,
      pressed: () => this.isVisible(region.id),
      toggle: () => void this.toggle(region.id)
    }));
  }

  #injectStyle(): void {
    if (typeof document === 'undefined') return;
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = layoutTogglesCss;
    document.head.appendChild(style);
  }
}

export const layoutToggles = new LayoutTogglesController();
export default layoutToggles;
